<script>
import { INDICE_MENSAGENS } from '@modules/aawz-intelligence-v2/constants/TextChat'

// Mesmo atraso do popover de Fontes (`chat-message-fontes.vue`): só no FECHAR,
// para o mouse conseguir atravessar o vão entre a coluna e o popover.
const ATRASO_PARA_FECHAR = 125

// Respiro entre a coluna de traços e o popover, que abre para a ESQUERDA dela.
const FOLGA_ATE_A_COLUNA = 8

export default {
  name: 'IntelligenceV2ChatIndiceMensagens',
  props: {
    // Só as mensagens do USUÁRIO, já preparadas pelo pai: `[{ id, texto }]`.
    // O `id` é o mesmo `data-mensagem-id` que o trilho carimba em cada bolha —
    // é por ele que o `rolar` acha o alvo.
    mensagens: {
      type: Array,
      default: function () {
        return []
      },
    },
    // A mensagem que o leitor está lendo — decidida pelo pai a partir do
    // scroll (`utils/indice-mensagens/mensagem-ativa.js`).
    idAtiva: {
      type: [String, Number],
      default: '',
    },
  },
  data() {
    return {
      ATRASO_PARA_FECHAR,
      FOLGA_ATE_A_COLUNA,
      titulo: INDICE_MENSAGENS.titulo,
      // O `open-on-hover` da Vuetify NÃO liga o clique no ativador; sem este
      // modelo a coluna não abriria por teclado.
      aberto: false,
    }
  },
  computed: {
    linhas() {
      const idAtiva = this.idAtiva
      return this.mensagens.map(function (mensagem) {
        return {
          id: mensagem.id,
          // Uma linha só: a quebra de parágrafo do balão viraria altura extra
          // no `text-truncate`, que só corta no eixo horizontal.
          texto: String(mensagem.texto || '')
            .replace(/\s+/g, ' ')
            .trim(),
          ativa: mensagem.id === idAtiva,
        }
      })
    },
    temLinhas() {
      return this.linhas.length > 0
    },
    rotuloDoGatilho() {
      return INDICE_MENSAGENS.gatilho(this.linhas.length)
    },
  },
  methods: {
    alternar() {
      this.aberto = !this.aberto
    },
    rolar(id) {
      this.$emit('rolar', id)
      this.aberto = false
    },
  },
}
</script>

<template>
  <div v-if="temLinhas" class="chat-indice-mensagens">
    <v-menu
      v-model="aberto"
      open-on-hover
      left
      offset-x
      :nudge-left="FOLGA_ATE_A_COLUNA"
      :close-on-content-click="false"
      :close-delay="ATRASO_PARA_FECHAR"
      content-class="ia-fontes-menu"
      min-width="220"
      max-width="300"
    >
      <template v-slot:activator="{ on, attrs }">
        <!-- Um ÚNICO ativador para a coluna inteira; os traços são só desenho.
             `<button>` e não `<div role="button">`: foco, Enter e Espaço vêm
             de graça — mesmo molde do chip de Fontes. -->
        <button
          type="button"
          class="chat-indice-mensagens__coluna"
          :aria-label="rotuloDoGatilho"
          :aria-expanded="aberto ? 'true' : 'false'"
          v-bind="attrs"
          v-on="on"
          @click="alternar"
        >
          <span
            v-for="linha in linhas"
            :key="linha.id"
            class="chat-indice-mensagens__traco"
            :class="{ 'chat-indice-mensagens__traco--ativo': linha.ativa }"
            aria-hidden="true"
          />
        </button>
      </template>

      <!-- Declarado NESTE template, então carrega o `data-v` do scope e o CSS
           scoped o alcança mesmo com o `v-menu` teleportando o conteúdo para
           fora. O `.v-menu__content` é da Vuetify — por isso a casca é a
           partial global `ia-fontes-menu`, a mesma do popover de Fontes. -->
      <div class="chat-indice-mensagens__painel">
        <div class="chat-indice-mensagens__painel-topo ia-texto-selo">{{ titulo }}</div>
        <ul class="chat-indice-mensagens__lista">
          <li v-for="linha in linhas" :key="linha.id">
            <button
              type="button"
              class="chat-indice-mensagens__item ia-texto-auxiliar text-truncate"
              :class="{ 'chat-indice-mensagens__item--ativo': linha.ativa }"
              :title="linha.texto"
              :aria-current="linha.ativa ? 'true' : null"
              @click="rolar(linha.id)"
            >
              {{ linha.texto }}
            </button>
          </li>
        </ul>
      </div>
    </v-menu>
  </div>
</template>

<style lang="sass" scoped>
.chat-indice-mensagens
  // O `v-menu` embrulha o ativador num `div.v-menu` de 0×0 que ainda ocupa
  // lugar no layout — o mesmo remédio de `chat-message-fontes.vue`.
  ::v-deep .v-menu
    display: contents

// Reset do `<button>` + a pilha de traços. A área clicável é maior que os
// traços de propósito (padding): 2px de altura não é alvo de mouse.
.chat-indice-mensagens__coluna
  display: flex
  flex-direction: column
  align-items: flex-end
  gap: 6px
  padding: 8px 4px
  border: 0
  border-radius: var(--radius-sm)
  background: transparent
  cursor: pointer

  &:focus-visible
    outline: 2px solid var(--primary)
    outline-offset: 2px

.chat-indice-mensagens__traco
  display: block
  width: 16px
  height: 2px
  border-radius: 999px
  background: var(--neutral-300)
  transition: width 0.15s ease, background 0.15s ease

  &--ativo
    width: 24px
    background: var(--neutral-700)

// Cabeçalho quente + corpo claro: o par de `chat-message-fontes__painel-topo`.
.chat-indice-mensagens__painel-topo
  color: var(--neutral-700)
  padding: 8px 12px
  background: var(--sidebar-warm-bg)
  border-bottom: 1px solid var(--sidebar-warm-border)
  text-transform: uppercase
  letter-spacing: 0.03em

.chat-indice-mensagens__lista
  list-style: none
  margin: 0
  padding: 4px 0
  max-height: 280px
  overflow-y: auto
  background: var(--ia-content-bg)

.chat-indice-mensagens__item
  color: var(--neutral-900)
  display: block
  width: 100%
  padding: 6px 12px
  border: 0
  background: transparent
  text-align: left
  cursor: pointer
  transition: background 0.15s ease

  &:hover,
  &:focus-visible
    background: var(--sidebar-warm-hover)
    outline: 0

  &--ativo
    background: var(--sidebar-warm-bg)
    font-weight: 600
</style>
